/** Origen del ítem: inventario propio o API de mayorista. */
export type OrigenProveedor = 'alarcom' | 'tvc' | 'syscom';

export interface Producto {
  /** ID público: id_pto o "tvc-123" / "syscom-456" */
  catalog_id?: string;
  id_pto: number | null;
  modelo_pto: string;
  nombre_pto: string;
  descripcion_pto?: string;
  descripcion_web?: string | null;
  precio_venta: number;
  precio_oferta?: number | null;
  imagen?: string | null;
  categoria?: string | null;
  marca?: string | null;
  sku?: string | null;
  existencias: number;
  visible_ecommerce: boolean;
  clave_prod_serv?: string | null;
  /** Interno: checkout/admin. No mostrar en UI de tienda. */
  origen?: OrigenProveedor;
  id_externo?: string | null;
  sku_externo?: string | null;
}

export interface ProductoProveedor extends Producto {
  origen: OrigenProveedor;
  id_externo: string;
  sku_externo: string;
  modelo_fabricante?: string;
  category_id?: string | null;
  categoria_breadcrumb?: string | null;
  precio_lista?: number | null;
  precio_distribuidor?: number | null;
  moneda?: string;
}

export interface CategoriaProveedor {
  proveedor: OrigenProveedor;
  id_externo: string;
  nombre: string;
  parent_id?: string | null;
  breadcrumb?: string | null;
  imagen_url?: string | null;
  productos_count?: number | null;
  children?: CategoriaProveedor[];
}

export interface KitProducto {
  id_pto: number;
  modelo_pto: string;
  nombre_pto: string;
  cantidad: number;
  precio_unitario?: number;
}

export interface Kit {
  id_kit: number;
  nombre: string;
  slug: string;
  descripcion: string;
  descripcion_web?: string | null;
  precio: number;
  precio_oferta?: number | null;
  imagen?: string | null;
  tipo_venta_producto?: string | null;
  activo: boolean;
  productos: KitProducto[];
  existencias_disponibles?: number;
}

export interface Categoria {
  slug: string;
  nombre: string;
  cantidad: number;
}

export interface CartItem {
  id: string;
  tipo: 'producto' | 'kit';
  /** id_pto local, o 0 si es solo externo */
  refId: number;
  nombre: string;
  imagen?: string | null;
  precio: number;
  cantidad: number;
  esKit?: boolean;
  /** Obligatorio para checkout multi-proveedor */
  origen?: OrigenProveedor;
  idExterno?: string | null;
  skuExterno?: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  meta?: Record<string, unknown>;
}

export interface CheckoutForm {
  nombre: string;
  email: string;
  telefono: string;
  calle: string;
  numero: string;
  colonia: string;
  municipio: string;
  estado: string;
  codigo_postal: string;
  requiere_factura: boolean;
  rfc_fiscal?: string;
  razon_social_fiscal?: string;
  uso_cfdi?: string;
  regimen_fiscal?: string;
  codigo_postal_fiscal?: string;
}

export interface BannerSlide {
  id: number;
  etiqueta: string | null;
  titulo: string | null;
  subtitulo: string | null;
  imagenDesktop: string;
  imagenMovil: string | null;
  urlDestino: string | null;
  btn1: { texto: string; url: string } | null;
  btn2: { texto: string; url: string } | null;
}

export type PropositoAcceso = 'activar' | 'recuperar';

export interface PrepararCuentaResponse {
  nombre: string;
  email_enmascarado: string;
  proposito: PropositoAcceso;
}

export interface ConfirmarCorreoResponse {
  enviado: boolean;
  mensaje?: string;
  expira_minutos?: number;
}

export interface VerificarCorreoResponse {
  existe: boolean;
  mensaje?: string | null;
}

export type ModoCompra = 'invitado' | 'cliente';

export interface ClientePerfil {
  id_cliente: number;
  cuenta: string;
  nombre: string;
  email: string | null;
  telefono: string | null;
  calle: string | null;
  numero: string | null;
  colonia: string | null;
  municipio: string | null;
  ciudad: string | null;
  estado: string | null;
  codigo_postal: string | null;
  rfc_fiscal: string | null;
  razon_social_fiscal: string | null;
  uso_cfdi: string | null;
  regimen_fiscal: string | null;
  codigo_postal_fiscal: string | null;
  tiene_facturacion: boolean;
}

export interface PedidoFactura {
  estatus: 'facturada' | 'pendiente' | 'espera_pago' | 'faltan_datos' | 'no_solicitada' | 'no_disponible';
  puede_timbrar: boolean;
  motivo: string | null;
  uuid: string | null;
  serie: string | null;
  folio: string | null;
  rfc: string | null;
  razon_social: string | null;
  uso_cfdi: string;
  tiene_xml: boolean;
  tiene_pdf: boolean;
  id_factura: number | null;
  catalogo_uso_cfdi?: Array<{ codigo: string; etiqueta: string }>;
}

export interface PedidoCliente {
  folio_web: string;
  estatus: string;
  estatus_envio: string;
  subtotal: number;
  iva: number;
  total: number;
  requiere_factura: boolean;
  id_venta: number | null;
  creado_en: string;
  cantidad_articulos: number;
  metodo_pago?: string | null;
  factura?: PedidoFactura;
}

export interface PedidoLinea {
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

export interface PedidoDetalle extends PedidoCliente {
  envio: Record<string, string>;
  items: PedidoLinea[];
}

export interface OpenpayTarjeta {
  id: string;
  marca: string;
  tipo: string;
  terminacion: string;
  mes: string;
  anio: string;
  titular: string;
  banco: string;
}

export interface AuthLoginResponse {
  token: string;
  cliente: ClientePerfil;
}

export interface HistorialAlarcomItem {
  folio?: number;
  fecha?: string | null;
  tipo?: string;
  total?: number;
  estatus: string;
  plan?: string;
}

export interface HistorialAlarcom {
  resumen: {
    compras: number;
    monitoreos_activos: number;
    monitoreos_inactivos: number;
    servicios: number;
    rma: number;
  };
  compras: HistorialAlarcomItem[];
  monitoreos: HistorialAlarcomItem[];
  servicios: HistorialAlarcomItem[];
}

export interface AuthMeResponse {
  cliente: ClientePerfil;
  pedidos: PedidoCliente[];
  historial?: HistorialAlarcom;
}

export const CATEGORIAS_DEFAULT: Categoria[] = [
  { slug: 'cctv', nombre: 'CCTV', cantidad: 0 },
  { slug: 'intrusion', nombre: 'Intrusión', cantidad: 0 },
  { slug: 'acceso', nombre: 'Control de acceso', cantidad: 0 },
  { slug: 'incendio', nombre: 'Incendio', cantidad: 0 },
  { slug: 'redes', nombre: 'Redes', cantidad: 0 },
  { slug: 'accesorios', nombre: 'Accesorios', cantidad: 0 },
];

export const TIPOS_SERVICIO = [
  'Intrusion',
  'CCTV',
  'Acceso Peatonal',
  'Incendio',
  'GPS',
  'Redes',
  'Acceso Vehicular',
  'Domotica',
  'Servicio',
] as const;
